/**
 * @format
 * @module lib/commercial/dfp-ads/header-bidding
 * @description Initializes the ad server, handles all header bidding requests, and controls when ad slots are rendered/displayed.
 * Order of operations for initializing the ad server:
 * 1. Attempt to load the Moat header script and the the Amazon Publisher Services Tag (apstag) library.
 * 2. Initialize Prebid: configure Prebid's global settings via "pbjs.bidderSettings" and "pbjs.setConfig()".
 * 3. Initialize GPT: stop GPT from loading via "googletag.pubads().disableInitialLoad()" before doing anything else with "googletag".
 * 4. Apply global, page-level targeting via "googletag.pubads().setTargeting()", "moatPrebidApi.setMoatTargetingForAllSlots()" and "apstag.setDisplayBids()".
 * 5. Enable all GPT services via "googletag.enableServices()".
 * 6. Resolve the "adServerReady" deferred Promise to notify other modules that the ad server is ready.
 * 7. Other modules will then be able to define ad slots via "googletag.defineSlot()" and call this module's "displayAdSlots()" function to initiate bidding and display.
 */
import pbjs from "prebid.js";
import "prebid.js/modules/appnexusBidAdapter";
import "prebid.js/modules/ixBidAdapter";
import "prebid.js/modules/medianetBidAdapter";
import "prebid.js/modules/sharethroughBidAdapter";

import tgamConfig from "config/tgam";
import { getStandardAdSizes } from "lib/commercial/dfp-ads/adslot-size-mapping";
import { gptEnableServices, gptSetTarget } from "lib/commercial/dfp-ads/gpt-ads";
import { permutiveSetTargets } from "lib/commercial/permutive";
import Deferred from "utils/Deferred";
import loadScript from "utils/load-script";
import { cacheToken, getProp } from "utils/utils";
import logger from "utils/logger";
import isNil from "lodash/isNil";

// Required to process existing pbjs.queue blocks and setup any further pbjs.queue execution
pbjs.processQueue();

const isProdOrStage = getProp(tgamConfig, "env.isProdOrStage", false);
const failsafeTimeout = Number(getProp(tgamConfig, "env.hbFailsafeTimeout", 3000));
const debugTag = "[DFP-ADS]";

// Each of these placement keys will monetize ad slots with the specified sizes
const sharethroughKeys = [
  { pkey: "ybsvwCj0GqY38kyYSRg49z6S", sizes: [[970, 90]] },
  {
    pkey: "Z6nblEwCjRCN4TYrzsCpiAy5",
    sizes: [
      [970, 250],
      [728, 90]
    ]
  },
  {
    pkey: "FhPjdbKNBKmmBAmiFdnT50yW",
    sizes: [
      [300, 600],
      [300, 250]
    ]
  }
];

// It's easier to compare the array values when they're converted into strings
const sharethroughKeyStrings = sharethroughKeys.map(item => {
  return { ...item, sizes: item.sizes.map(size => JSON.stringify(size)) };
});

/**
 * Return the appropriate Sharethrough placement key for the given ad slot size, if one exists
 * @param {Array} size - One of the sizes from an ad slot's sizemap (e.g. [728, 90])
 * @returns {String|undefined} The matching Sharethrough placement key or undefined
 */
function getSharethroughKeyForAdSize(size) {
  const sizeString = JSON.stringify(size);
  const key = sharethroughKeyStrings.find(item => {
    const match = item.sizes.includes(sizeString);
    if (match) {
      logger.info(`${debugTag} Sharethrough key monetizes one of the ad slot's sizes: ${sizeString}`, item);
    }
    return match;
  });
  return key?.pkey;
}

const settings = {
  // Global header bidding settings
  lazyAdsEnabled: getProp(tgamConfig, "env.hbLazyAdsEnabled", false),
  refreshingAdsEnabled: getProp(tgamConfig, "env.hbRefreshingAdsEnabled", false),
  failsafeTimeout,
  requestTimeout: Number(getProp(tgamConfig, "env.hbRequestTimeout", 1000)),

  // Index Exchange Identity Library-specific settings
  ixIdentityLibEnabled: getProp(tgamConfig, "env.hbIXIdentityLibEnabled", false),
  ixIdentityLibTimeout: Number(getProp(tgamConfig, "env.hbIXIdentityLibTimeout", failsafeTimeout)),

  // Moat-specific settings
  moatHeaderEnabled: getProp(tgamConfig, "env.hbMoatHeaderEnabled", false),
  moatHeaderTimeout: Number(getProp(tgamConfig, "env.hbMoatHeaderTimeout", failsafeTimeout)),

  // APSTag-specific settings
  apsTagEnabled: getProp(tgamConfig, "env.hbAPSTagEnabled", false),

  // Prebid-specific settings
  prebidEnabled: getProp(tgamConfig, "env.hbPrebidEnabled", false),
  prebidUserSyncDelay: Number(getProp(tgamConfig, "env.hbPrebidUserSyncDelay", 6000)),
  prebidAppnexusBidCpmAdjustment: Number(
    getProp(tgamConfig, "env.hbPrebidAppNexusCpmAdjustment", 0.85)
  )
};

const prebidOptions = {
  userSyncDelay: settings.prebidUserSyncDelay,
  appnexusBidCpmAdjustment: settings.prebidAppnexusBidCpmAdjustment,
  appnexusPlacementId: isProdOrStage ? 17999567 : 13232392,
  ixSiteId: isProdOrStage ? 430074 : 123456,
  debug: getProp(tgamConfig, "env.hbPrebidDebug", logger.debugEnabled),
  medianetCid: "8CUM4F58G",
  medianetCrid: "586981315",
  priceGranularity: {
    buckets: [
      {
        precision: 2,
        max: 4,
        increment: 0.01
      },
      {
        precision: 2,
        max: 10,
        increment: 0.05
      },
      {
        precision: 2,
        max: 40,
        increment: 0.5
      },
      {
        precision: 2,
        max: 100,
        increment: 1.0
      }
    ]
  }
};

// Keep track of individual header bidding requests in order to avoid race conditions
const requestManager = {
  timeout: settings.requestTimeout,
  requests: [],
  create(dynamicAds, refresh) {
    const request = {
      id: cacheToken(),
      dynamicAds,
      refresh,
      apstagDone: false,
      prebidDone: false
    };
    this.requests.push(request);
    logger.info(`${debugTag} requestManager: new request`, JSON.parse(JSON.stringify(request)));
    logger.info(
      `${debugTag} requestManager: ${this.requests.length} active requests: ${this.requests
        .map(r => r.id)
        .toString()}`
    );
    return request;
  },
  destroy(requestID) {
    const index = this.requests.findIndex(req => req.id === requestID);
    if (index !== -1) {
      logger.info(
        `${debugTag} requestManager: destroying request`,
        JSON.parse(JSON.stringify(this.requests[index]))
      );
      this.requests.splice(index, 1);
      logger.info(
        `${debugTag} requestManager: ${this.requests.length} active requests: ${this.requests
          .map(r => r.id)
          .toString()}`
      );
    }
  }
};

/**
 * Promise always fired when the ad server has been initialized
 * @returns {Promise}
 */
export const adServerReady = new Deferred();

/**
 * Load the Index Exchange Identity Library
 * @see https://kb.indexexchange.com/IX_Library/Identity_Library.htm
 * @returns {Promise} that will always resolve, even when the script fails to load
 */
function initIndexExchangeIdentityLibrary() {
  logger.info(`${debugTag} initializing Index Exchange Identity Library`);

  return new Promise(resolve => {
    let timeoutId;
    let aborted = false;

    if (settings.ixIdentityLibEnabled === false) {
      abort();
      return;
    }
    loadScript("https://js-sec.indexww.com/ht/p/181495-30166317004832.js")
      .then(() => {
        clearTimeout(timeoutId);

        if (aborted) {
          // Timed out. Promise should have already been resolved via the abort()
          // function (which is how "aborted" got set to "true"), but this code is
          // still executing because the loadScript() operation was not canceled.
          return;
        }
        const headerTagExists =
          window.headertag && typeof window.headertag.getIdentityInfo === "function";
        if (headerTagExists) {
          // TODO: do I need to fetch the data and pass it along to the bidders
          // somehow, or will the Prebid adapters handle this for us?
          // const rtiData = window.headertag.getIdentityInfo();
        }
        logger.info(
          `${debugTag} Index Exchange Identity Library loaded (headertag exists: ${headerTagExists})`
        );
        resolve();
      })
      .catch(e => {
        logger.info(`${debugTag} Index Exchange Identity Library failed to load`, e);
        clearTimeout(timeoutId);
        abort();
      });

    timeoutId = setTimeout(() => {
      logger.info(`${debugTag} Index Exchange Identity Library timed out`);
      abort();
    }, settings.ixIdentityLibTimeout);

    // Set "aborted" to "true" so that subsequent functions will know that the script failed to load
    function abort() {
      aborted = true;
      resolve();
    }
  });
}

/**
 * Configure Moat header wrapper
 * @see https://jira.theglobeandmail.com/browse/ARC-5675
 * @see https://jira.theglobeandmail.com/secure/attachment/53387/YieldIntelligence-eng-docs_final_3.7.19_v2.pdf
 * @returns {Promise} that will always resolve, even when the script fails to load
 */
function initMoatHeader() {
  logger.info(`${debugTag} initializing Moat Header`);

  return new Promise(resolve => {
    let timeoutId;

    if (settings.moatHeaderEnabled === false) {
      abort();
      return;
    }
    loadScript("https://z.moatads.com/globeandmailheader638913312107/moatheader.js")
      .then(() => {
        clearTimeout(timeoutId);

        if (window.moatYieldReady === false) {
          // Timed out. Promise should have already been resolved via the abort()
          // function, but this code is still executing because the loadScript()
          // operation was not canceled.
          return;
        }
        logger.info(`${debugTag} Moat Header script loaded`);

        if (window.moatPrebidApi && logger.debugEnabled) {
          window.moatPrebidApi.enableLogging();
        }
      })
      .catch(e => {
        logger.info(`${debugTag} Moat Header script failed to load`, e);
        clearTimeout(timeoutId);
        abort();
      });

    // Unless the timeout occurs first, the Moat Header script will execute this
    // callback when viewability, invalid traffic and brand safety data is available
    window.moatYieldReady = () => {
      clearTimeout(timeoutId);
      resolve();
    };

    // Set "moatYieldReady" to "false" so that subsequent functions will know that the script failed to load
    function abort() {
      window.moatYieldReady = false;
      resolve();
    }

    timeoutId = setTimeout(() => {
      logger.info(`${debugTag} Moat Header callback timed out`);
      abort();
    }, settings.moatHeaderTimeout);
  });
}

/**
 * Load and initialize the Amazon Publisher Services Tag (apstag) library
 * @see https://aps.amazon.com/aps/
 * @see https://ams.amazon.com/webpublisher/uam/docs/aps-debug-console.html
 * @returns {Promise} that will always resolve, even when the script fails to load
 */
function initAPSTag() {
  logger.info(`${debugTag} initializing APSTag`);

  return new Promise(resolve => {
    let timeoutId;

    if (settings.apsTagEnabled === false) {
      resolve();
      return;
    }
    loadScript("https://c.amazon-adsystem.com/aax2/apstag.js")
      .then(() => {
        logger.info(`${debugTag} APSTag script loaded`);
        clearTimeout(timeoutId);

        if (!window.apstag) {
          resolve();
        } else {
          const formattedSectionName = getProp(tgamConfig, "adops.formattedSectionName", "");
          const pp0 = getProp(tgamConfig, "adops.pageTargets.pp0", "");

          window.apstag.init(
            {
              pubID: "3947",
              adServer: "googletag",
              bidTimeout: requestManager.timeout,
              params: {
                si_section: formattedSectionName || pp0
              }
            },
            () => {
              logger.info(`${debugTag} APSTag script initialized`);
              resolve();
            }
          );
        }
      })
      .catch(e => {
        logger.info(`${debugTag} APSTag script failed to load`, e);
        clearTimeout(timeoutId);
        resolve();
      });

    timeoutId = setTimeout(() => {
      logger.info(`${debugTag} APSTag script loading timed out`);
      resolve();
    }, failsafeTimeout);
  });
}

/**
 * Configure Prebid with global settings
 * @see http://prebid.org/dev-docs/publisher-api-reference.html#module_pbjs.setConfig
 */
function initPrebid() {
  logger.info(`${debugTag} initializing Prebid`);

  if (settings.prebidEnabled === false) {
    return;
  }

  let mCategoriesKeys;
  let mSafetyKeys;

  if (window.moatYieldReady !== false && window.moatPrebidApi) {
    mCategoriesKeys = window.moatPrebidApi.getMoatTargetingForPage().m_categories;
    mSafetyKeys = window.moatPrebidApi.getMoatTargetingForPage().m_safety;
  }

  pbjs.bidderSettings = {
    appnexus: {
      adserverTargeting: [
        {
          key: "apn_buyer_memberid",
          val: bidResponse => {
            return bidResponse.appnexus.buyerMemberId;
          }
        },
        {
          key: "apn_prio",
          val: bidResponse => {
            return bidResponse.appnexus.dealPriority;
          }
        },
        {
          key: "apn_dealcode",
          val: bidResponse => {
            return bidResponse.appnexus.dealCode;
          }
        }
      ],
      bidCpmAdjustment: bidCpm => {
        return bidCpm * prebidOptions.appnexusBidCpmAdjustment;
      }
    }
  };

  let configs = {
    // Enable our custom pricing granularity bucket
    priceGranularity: prebidOptions.priceGranularity,
    debug: prebidOptions.debug,
    userSync: {
      filterSettings: {
        all: {
          bidders: "*",
          filter: "include"
        }
      },
      // Perform the sync X seconds after the auction to avoid using up browser sockets for a while
      syncDelay: prebidOptions.userSyncDelay
    }
  };

  // IndexExchange first-party data
  const fpd = { ix: { firstPartyData: {} } };
  const admants = getProp(tgamConfig, "adops.admantx.admants");

  // If getMoatTargetingForPage() returns empty array, null, or is undefined then don't set the following values
  if (!isNil(mCategoriesKeys) && mCategoriesKeys.length) {
    fpd.ix.firstPartyData.m_categories = mCategoriesKeys;
    fpd.ix.firstPartyData.m_safety = mSafetyKeys;
  }
  // TO DO: remove later
  if (window.Krux && (window.Krux.segments || window.Krux.user)) {
    fpd.ix.firstPartyData.kuid = window.Krux.segments || "none";
    fpd.ix.firstPartyData.ksg = window.Krux.user || "none";
  }
  if (admants) {
    fpd.ix.firstPartyData.ias_admants = admants;
  }
  // If at least one of the object's properties is not nil
  if (Object.values(fpd.ix.firstPartyData).filter(v => !isNil(v)).length) {
    configs = { ...configs, ...fpd };
  }

  pbjs.que.push(() => {
    pbjs.setConfig(configs);
  });
  logger.info(`${debugTag} Prebid config:`, pbjs.getConfig());
}

/**
 * Initialize the ad server
 * @see https://developers.google.com/doubleclick-gpt/reference
 * @see https://ams.amazon.com/webpublisher/uam/docs/web-integration-documentation/integration-guide/javascript-guide/
 */
function initServer() {
  if (pbjs.initAdserverSet) {
    return;
  }
  pbjs.initAdserverSet = true;
  logger.info(`${debugTag} initializing ad server`);

  window.googletag = window.googletag || {};
  window.googletag.cmd = window.googletag.cmd || [];

  // Because we may need to wait for Prebid and/or APSTag to return bids before
  // we can display certain ads, we must prevent GPT from making ad requests on
  // initial page load (globally). It's important for "disableInitialLoad()" to
  // be the first entry in the "googletag.cmd" array, so that it executes first:
  // https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService_disableInitialLoad
  window.googletag.cmd.push(() => {
    logger.info(`${debugTag} disabling GPT initial load`);
    window.googletag.pubads().disableInitialLoad();

    // Apply page-level Moat targets to all ad slots, if the Moat header script has initialized
    if (window.moatYieldReady !== false && window.moatPrebidApi) {
      window.moatPrebidApi.setMoatTargetingForAllSlots();
      logger.info(
        `${debugTag} setting Moat page targets:`,
        window.moatPrebidApi.getMoatTargetingForPage()
      );
    }
    // Apply page-level AdOps targets to all ad slots
    const pageTargets = getProp(tgamConfig, "adops.pageTargets", {});
    logger.groupCollapsed(`${debugTag} setting GPT page targets`);

    for (let prop in pageTargets) {
      gptSetTarget(prop, pageTargets[prop]);
    }

    // To Do: remove later
    // Apply page-level Krux targets to all ad slots
    if (window.Krux) {
      gptSetTarget("ksg", window.Krux.segments);
      gptSetTarget("kuid", window.Krux.user);
    }

    // Apply page-level Permutive targets to all ad slots
    permutiveSetTargets();

    logger.groupEnd(`${debugTag} setting GPT page targets`);

    // The APSTag docs say that "apstag.setDisplayBids() must be called after all
    // googletag slots have been defined, and before googletag.enableServices()",
    // then again whenever fresh bids are returned. Since none of our ad slots
    // get created/defined until after the "adServerReady" Promise has resolved,
    // executing this method here may be unnecessary?
    // https://ams.amazon.com/webpublisher/uam/docs/web-integration-documentation/integration-guide/javascript-guide/api-reference.html
    // https://ams.amazon.com/webpublisher/uam/docs/web-integration-documentation/integration-guide/javascript-guide/examples.html
    window.apstag && window.apstag.setDisplayBids();

    // After all page-level targeting has been set, enable all GPT services.
    // Because we aren't calling "googletag.pubads().enableSingleRequest()",
    // it should be safe to create/define all of our ad slots after "googletag.enableServices()"
    // executes. This is important because none of our ad slots get created/defined
    // (via "googletag.defineSlot()") until after the "adServerReady" Promise has resolved:
    // https://developers.google.com/doubleclick-gpt/common_implementation_mistakes#scenario-5:-mis-ordering-calls-to-gpt
    // https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService_enableSingleRequest
    gptEnableServices();

    // Individual ad slots won't be defined or displayed until after this point
    logger.info(`${debugTag} ad server initialized`);
    adServerReady.resolve();
  });
}

/**
 * Callback to execute when both the APSTag and Prebid responses have returned, for a given request
 * @param {Object} request - A requestManager request object
 * @param {Array<Object>} request.dynamicAds - Array of objects representing an ad
 * @param {Boolean} request.refresh - Whether to display the ads for the first time or refresh existing ads
 * @param {Boolean} request.apstagDone - True when APSTag has either timed out or returned with bids
 * @param {Boolean} request.prebidDone - True when Prebid has either timed out or returned with bids
 * @param {String} message - Context message to display, for logging purposes
 */
function biddersBack(request, message) {
  if (request.apstagDone && request.prebidDone) {
    logger.info(`${debugTag} both header bidding services are finished`);
    sendAdserverRequest(request, message);
  } else {
    logger.info(`${debugTag} still waiting for one of the header bidding services`);
  }
}

/**
 * Render all of the ad slots
 * @param {Object} request - A requestManager request object
 * @param {Array<Object>} request.dynamicAds - Array of objects representing an ad
 * @param {Boolean} request.refresh - Whether to display the ads for the first time or refresh existing ads
 * @param {Boolean} request.apstagDone - True when APSTag has either timed out or returned with bids
 * @param {Boolean} request.prebidDone - True when Prebid has either timed out or returned with bids
 * @param {String} message - Context message to display, for logging purposes
 */
function sendAdserverRequest(request, message) {
  request.dynamicAds.forEach(dynamicAd => {
    renderAdSlot(dynamicAd, request.refresh, message);
  });
  requestManager.destroy(request.id);
}

/**
 * Render an individual ad slot
 * @param {Object} dynamicAd - An object containing specific ad-related data
 * @param {Boolean} refresh - Whether to display the ad for the first time or refresh it
 * @param {String} message - Context message to display, for logging purposes
 */
function renderAdSlot(dynamicAd, refresh, message) {
  if (refresh) {
    logger.info(`${debugTag} refresh slot ${dynamicAd.id} ${message}`);
    dynamicAd.refreshSlot();
  } else {
    logger.info(`${debugTag} display slot ${dynamicAd.id} ${message}`);
    dynamicAd.displaySlot();
  }
}

/**
 * When the ad server is ready, make APSTag and Prebid requests for the appropriate ads, then render all of the given ad slots
 * @see https://ams.amazon.com/webpublisher/uam/docs/web-integration-documentation/integration-guide/javascript-guide/
 * @see http://prebid.org/dev-docs/publisher-api-reference.html#module_pbjs.requestBids
 * @see http://prebid.org/dev-docs/publisher-api-reference.html#module_pbjs.setTargetingForGPTAsync
 * @see http://prebid.org/troubleshooting/troubleshooting-guide.html
 * @param {Array<Object>} dynamicAds - One or more objects containing specific ad-related data
 * @param {Boolean} [refresh=false] - Whether to display an ad for the first time or refresh an existing ad
 */
export function displayAdSlots(dynamicAds, refresh = false) {
  adServerReady.then(() => {
    // Render non-Prebid-eligible ads immediately, then filter them out of the array
    const prebidEligibleAds = dynamicAds.filter(dynamicAd => {
      const sizemapForTier = dynamicAd.sizemapForTier.sizes;
      const filteredSizes = getStandardAdSizes(sizemapForTier);
      const hasValidAdSizes = filteredSizes.length > 0;
      const isEligibleForBidding = dynamicAd.prebid && hasValidAdSizes;

      // Set Moat targeting, if the Moat header script exists
      if (window.moatYieldReady !== false && window.moatPrebidApi) {
        window.moatPrebidApi.setMoatTargetingForSlot(dynamicAd.slot);
        logger.info(
          `${debugTag} setting Moat targets for slot ${dynamicAd.id}:`,
          window.moatPrebidApi.getMoatTargetingForSlot(dynamicAd.slot)
        );
      }
      // Allow non-Prebid-eligible ads to display as soon as the ad server is
      // ready, instead of waiting for a APSTag and Prebid requests
      if (!isEligibleForBidding) {
        renderAdSlot(dynamicAd, refresh, "without bidding (not eligible)");
        return false;
      }
      // Otherwise, prepare a sizemap for the APSTag and Prebid requests
      if (refresh && dynamicAd.lastSize.length) {
        // If refreshing, then reuse the same size as the previously-rendered ad
        dynamicAd.sizemapForPrebid = [dynamicAd.lastSize];
      } else {
        dynamicAd.sizemapForPrebid = filteredSizes;
      }
      return true;
    });

    // If none of the supplied ads are eligible for bidding, then the above code
    // should have already triggered them to render, so our work here is done
    if (!prebidEligibleAds.length) {
      logger.info(`${debugTag} no ads queued for bidding`);
      return;
    }
    // Otherwise, add the ads to the APSTag and Prebid queues and create
    // a "request" object to keep track of the statuses of those requests
    const request = requestManager.create(prebidEligibleAds, refresh);
    const adUnitCodes = prebidEligibleAds.map(dynamicAd => dynamicAd.id);
    logger.info(`${debugTag} ads queued for bidding:`, adUnitCodes);

    // APSTag bidding
    if (window.apstag) {
      logger.info(`${debugTag} requesting bids from APSTag (request ID: ${request.id})`);

      window.apstag.fetchBids(
        {
          slots: prebidEligibleAds.map(dynamicAd => {
            return {
              slotID: dynamicAd.id,
              slotName: dynamicAd.id,
              sizes: dynamicAd.sizemapForPrebid
            };
          })
        },
        bids => {
          logger.info(`${debugTag} bids returned from APSTag:`, bids);

          window.googletag.cmd.push(() => {
            // Apply APSTag targets to the ad slots that were bid on,
            // regardless of whether any of them received a bid
            window.apstag.setDisplayBids();
            request.apstagDone = true;
            logger.info(`${debugTag} APSTag request finished`, JSON.parse(JSON.stringify(request)));
            biddersBack(request, "with possible bid");
          });
        }
      );
    } else {
      logger.info(`${debugTag} not requesting bids from APSTag because it's unavailable`);
      request.apstagDone = true; // See biddersBack()
    }

    // Prebid bidding
    pbjs.que.push(() => {
      prebidEligibleAds.forEach(dynamicAd => {
        if (!refresh) {
          pbjs.addAdUnits(definePrebidAdUnit(dynamicAd));
        } else if (refresh && dynamicAd.lastSize.length) {
          // If refreshing, re-register the ad unit with Prebid because its sizemap may have changed
          pbjs.removeAdUnit(dynamicAd.id);
          pbjs.addAdUnits(definePrebidAdUnit(dynamicAd));
        }
      });
      logger.info(`${debugTag} requesting bids from Prebid (request ID: ${request.id})`);

      pbjs.requestBids({
        adUnitCodes,
        timeout: requestManager.timeout,
        bidsBackHandler: bids => {
          // This callback function also receives a "timedOut" argument,
          // but this isn't useful because APSTag provides no equivalent
          // information about its bids
          logger.info(`${debugTag} bids returned from Prebid:`, bids);

          window.googletag.cmd.push(() => {
            // Apply Prebid targets exclusively to ad slots that received a bid
            const adUnitCodes = bids ? Object.keys(bids) : [];
            if (adUnitCodes.length) {
              pbjs.setTargetingForGPTAsync(adUnitCodes);
            }
            request.prebidDone = true;
            logger.info(`${debugTag} Prebid request finished`, JSON.parse(JSON.stringify(request)));
            // Pass the entire "prebidEligibleAds" array to the handler function
            // because all of the Prebid-enabled ads will need to be displayed
            // regardless of whether they were bid on, plus we need to track
            // whether they're loading for the first time or subsequently refreshing
            biddersBack(request, "with possible bid");
          });
        }
      });
    });
  });
}

/**
 * Give Prebid the ad units that we expect to make requests for (non-standard ad sizes will be filtered out)
 * @see http://prebid.org/dev-docs/publisher-api-reference.html#module_pbjs.addAdUnits
 * @see http://prebid.org/dev-docs/bidders.html
 * @param {Object} dynamicAd - An object containing specific ad-related data
 * @returns {Object} An ad unit object to be supplied to "pbjs.addAdUnits()"
 */
function definePrebidAdUnit(dynamicAd) {
  const code = dynamicAd.id;
  const sizes = dynamicAd.sizemapForPrebid;
  const sharethroughKeysUsed = []; // Avoid using the same key twice, for a given ad slot
  const bids = [
    {
      bidder: "appnexus",
      params: {
        placementId: prebidOptions.appnexusPlacementId
      }
    },
    {
      bidder: "medianet",
      params: {
        cid: prebidOptions.medianetCid,
        crid: prebidOptions.medianetCrid
      }
    }
  ];
  sizes.forEach(sm => {
    bids.push({
      bidder: "ix",
      params: {
        siteId: prebidOptions.ixSiteId,
        size: sm
      }
    });
    const pkey = getSharethroughKeyForAdSize(sm);
    if (pkey && !sharethroughKeysUsed.includes(pkey)) {
      sharethroughKeysUsed.push(pkey);
      bids.push({
        bidder: "sharethrough",
        params: {
          pkey
        }
      });
    }
  });
  const adUnit = {
    code,
    mediaTypes: {
      banner: {
        sizes
      },
      native: {
        title: {
          required: true
        },
        body: {
          required: true
        },
        image: {
          required: true
        },
        sponsoredBy: {
          required: false
        },
        icon: {
          required: false
        }
      }
    },
    bids
  };
  logger.info(`${debugTag} ad formatted for Prebid request:`, adUnit);
  return adUnit;
}

/**
 * Initialize Moat, APSTag, Prebid and GPT
 */
export default function initAdServer() {
  logger.info(`${debugTag} initAdServer`, settings);

  initIndexExchangeIdentityLibrary().then(() => {
    Promise.all([initMoatHeader(), initAPSTag()]).then(() => {
      initPrebid();
      initServer();
    });
  });
}
